import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

const Pricing: React.FC = () => {
  const { t, language } = useLanguage();

  const solutions = [
    {
      id: 'basic',
      title: language === 'en' ? 'Small Business' : 'Kisvállalkozás',
      description: language === 'en' 
        ? 'Perfect for small businesses with basic CRM needs' 
        : 'Tökéletes kisvállalkozásoknak alapvető CRM igényekkel',
      features: language === 'en'
        ? [
            'Customer database management',
            'Template creation',
            'Document storage',
            'Basic quotation management',
            'Mobile access',
            'Email support'
          ]
        : [
            'Ügyfél adatbázis kezelés',
            'Sablon létrehozás',
            'Dokumentum tárolás',
            'Alapvető árajánlat kezelés',
            'Mobil hozzáférés',
            'Email támogatás'
          ],
      icon: (
        <svg className="h-8 w-8" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
    },
    {
      id: 'pro',
      title: language === 'en' ? 'Business' : 'Vállalkozás',
      description: language === 'en'
        ? 'For growing businesses with advanced needs'
        : 'Növekvő vállalkozásoknak fejlettebb igényekkel',
      features: language === 'en'
        ? [
            'Unlimited customer records',
            'Advanced document management',
            'Custom templates',
            'Contract management',
            'Worksheet management',
            'Basic ticketing system',
            'Sales process automation',
            'Advanced reporting',
            'Basic data migration',
            'Priority support',
            'API access'
          ]
        : [
            'Korlátlan ügyfél nyilvántartás',
            'Fejlett dokumentumkezelés',
            'Egyedi sablonok',
            'Szerződéskezelés',
            'Munkalap kezelés',
            'Alapvető jegykezelő rendszer',
            'Értékesítési folyamat automatizálás',
            'Fejlett jelentések',
            'Alapvető adatmigráció',
            'Kiemelt támogatás',
            'API hozzáférés'
          ],
      icon: (
        <svg className="h-8 w-8" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
        </svg>
      ),
      popular: true,
    },
    {
      id: 'enterprise',
      title: language === 'en' ? 'Enterprise' : 'Nagyvállalat',
      description: language === 'en'
        ? 'For large organizations with complex requirements'
        : 'Nagy szervezeteknek összetett követelményekkel',
      features: language === 'en'
        ? [
            'Fully customized solution',
            'Enterprise-grade security',
            'User role management',
            'Advanced worksheet management',
            'Advanced ticketing module',
            'Workflow customization',
            'Integration with existing systems',
            'Custom reporting and dashboards',
            'Complete data migration service',
            '24/7 support',
            'Dedicated account manager',
            'On-premise deployment option'
          ]
        : [
            'Teljesen testreszabott megoldás',
            'Vállalati szintű biztonság',
            'Felhasználói szerepkör kezelés',
            'Fejlett munkalap kezelés',
            'Fejlett jegykezelő modul',
            'Munkafolyamat testreszabása',
            'Integráció meglévő rendszerekkel',
            'Egyedi jelentések és műszerfalak',
            'Teljes körű adatmigrációs szolgáltatás',
            '24/7 támogatás',
            'Dedikált ügyfélmenedzser',
            'Helyszíni telepítési lehetőség'
          ],
      icon: (
        <svg className="h-8 w-8" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      ),
    }
  ];

  return (
    <div className="bg-gradient-to-b from-white to-gray-50 py-16 sm:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative">
          {/* Decorative blobs */}
          <div className="absolute -top-24 -left-10 w-72 h-72 bg-indigo-100 rounded-full mix-blend-multiply opacity-20 animate-blob"></div>
          <div className="absolute -top-16 -right-10 w-72 h-72 bg-purple-100 rounded-full mix-blend-multiply opacity-20 animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-24 left-20 w-72 h-72 bg-pink-100 rounded-full mix-blend-multiply opacity-20 animate-blob animation-delay-4000"></div>
          
          <div className="relative text-center">
            <div className="inline-flex items-center justify-center px-4 py-1 mb-4 rounded-full bg-gradient-to-r from-indigo-100 to-purple-100 text-sm font-medium text-indigo-800">
              <span className="animate-pulse mr-2">✨</span>
              {language === 'en' ? 'More affordable than you think' : 'Elérhetőbb, mint gondolná'}
            </div>
            <h2 className="text-base font-semibold text-indigo-600 tracking-wide uppercase">
              {t('pricing.title')}
            </h2>
            <p className="mt-1 text-4xl font-extrabold text-gray-900 sm:text-5xl sm:tracking-tight">
              {t('pricing.subtitle')}
            </p>
            <p className="mt-4 max-w-2xl mx-auto text-xl text-gray-500">
              {language === 'en'
                ? 'Custom solutions with a focus on your most needed features. Pay only for what matters to your business.'
                : 'Egyedi megoldások az Ön számára legfontosabb funkciókra összpontosítva. Csak azért fizet, ami valóban számít vállalkozása számára.'}
            </p>
          </div>
        </div>

        <div className="mt-16 space-y-12 lg:space-y-0 lg:grid lg:grid-cols-3 lg:gap-8">
          {solutions.map((solution) => (
            <div key={solution.id} className={`relative p-8 rounded-2xl shadow-lg transition-all duration-500 hover:shadow-xl flex flex-col transform hover:-translate-y-1 ${solution.popular ? 'bg-gradient-to-br from-white via-white to-indigo-50 ring-2 ring-indigo-500' : 'bg-white'}`}>
              {solution.popular && (
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
                  <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-md">
                    {language === 'en' ? 'Most Popular' : 'Legnépszerűbb'}
                  </span>
                </div>
              )}
              <div className={`absolute top-6 right-6 p-2 rounded-full ${solution.popular ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-100 text-gray-500'}`}>
                {solution.icon}
              </div>
              
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-gray-900">{solution.title}</h3>
                <p className="mt-4 flex items-baseline">
                  <span className="text-3xl font-extrabold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-purple-600">
                    {language === 'en' ? 'Custom pricing' : 'Egyedi árazás'}
                  </span>
                </p>
                <p className="mt-2 text-gray-500 text-sm italic">
                  {language === 'en' ? 'Tailored to your needs' : 'Igényeire szabva'}
                </p>
                <p className="mt-4 text-gray-500">{solution.description}</p>

                <p className="mt-6 font-medium text-gray-900">
                  {language === 'en' ? 'Included features:' : 'Tartalmazott funkciók:'}
                </p>
                <ul role="list" className="mt-2 space-y-3">
                  {solution.features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <svg className={`flex-shrink-0 w-5 h-5 ${solution.popular ? 'text-indigo-500' : 'text-gray-400'}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="ml-2 text-gray-500">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <Link
                to="/contact"
                className="mt-8 block w-full py-3 px-6 border border-transparent rounded-md shadow-sm text-center font-medium text-base transition-all duration-300 bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-700 hover:to-purple-700 hover:shadow-lg"
              >
                {language === 'en' ? 'Request a Demo' : 'Demó kérése'}
              </Link>
            </div>
          ))}
        </div>
        
        <div className="mt-16 text-center">
          <div className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-full bg-indigo-50 text-indigo-800">
            <span className="mr-2">💡</span>
            {language === 'en'
              ? "We believe great CRM shouldn't break the bank"
              : "Hiszünk benne, hogy a nagyszerű CRM nem kerül vagyonokba"}
          </div>
          <p className="mt-4 text-gray-500 max-w-3xl mx-auto">
            {language === 'en'
              ? 'Each solution is priced based on the actual features you need and the size of your business. In most cases, clients find our pricing significantly more affordable than they anticipated.'
              : 'Minden megoldást az Ön által ténylegesen igényelt funkciók és vállalkozása mérete alapján árazunk. Az esetek többségében ügyfeleink jelentősen kedvezőbbnek találják árainkat, mint azt előzetesen gondolták volna.'}
          </p>
          <div className="mt-8">
            <Link 
              to="/contact" 
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-md text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 transition-all duration-300 transform hover:scale-105"
            >
              {language === 'en' ? 'Get Your Custom Quote Today' : 'Kérjen egyedi árajánlatot még ma'}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pricing; 